import { isInsideOnboardingAtom, isOnboardingAtom, isLoggedInAtom, presentAppDownloadPromptAtom } from 'app/atoms';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { isWhiteLabel } from 'app/utils/helpers';
import { useAtom, useAtomValue } from 'jotai';
import { lazy, Suspense, useEffect } from 'react';
import { Platform } from 'react-native';

const GUEST_PROMPT_DELAY_MS = 45_000;
const AppDownloadPrompt = lazy(() => import('app/components/app-download-prompt'));

export function AppDownloadPromptHost() {
  const isWhiteLabelBuild = isWhiteLabel();
  const [prompt, setPrompt] = useAtom(presentAppDownloadPromptAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isInsideOnboarding = useAtomValue(isInsideOnboardingAtom);
  const isOnboarding = useAtomValue(isOnboardingAtom);
  // Guests also have isOnboarding=true before they start signing up.
  const onboardingActive = isInsideOnboarding || (isLoggedIn && isOnboarding);
  const [guestPromptShown, setGuestPromptShown, , guestPreferenceLoaded] = useStoredValue<boolean>(
    StorageKey.AppDownloadGuestPromptShown,
    false,
  );

  useEffect(() => {
    if (
      onboardingActive ||
      isWhiteLabelBuild ||
      Platform.OS !== 'web' ||
      isLoggedIn ||
      !guestPreferenceLoaded ||
      guestPromptShown ||
      prompt
    )
      return;

    const timeout = setTimeout(() => {
      setPrompt((currentPrompt) => {
        if (currentPrompt) return currentPrompt;
        setGuestPromptShown(true);
        return { context: 'guest' };
      });
    }, GUEST_PROMPT_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [
    guestPreferenceLoaded,
    guestPromptShown,
    isLoggedIn,
    isWhiteLabelBuild,
    onboardingActive,
    prompt,
    setGuestPromptShown,
    setPrompt,
  ]);

  useEffect(() => {
    if (prompt && (onboardingActive || (prompt.context === 'guest' && isLoggedIn))) setPrompt(false);
  }, [isLoggedIn, onboardingActive, prompt, setPrompt]);

  if (onboardingActive || isWhiteLabelBuild || Platform.OS !== 'web' || !prompt) return null;

  return (
    <Suspense fallback={null}>
      <AppDownloadPrompt onClose={() => setPrompt(false)} prompt={prompt} />
    </Suspense>
  );
}
